import React, { useCallback, useEffect, useState } from "react";

import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import { formatDuration } from "date-fns/esm";
import { defineMessage, useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import FilterModel, {
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import RoundButton from "@app/component/RoundButton";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import getRangeByDuration from "../utils/getRangeByDuration";

type Duration = Parameters<typeof formatDuration>[0];

const CLEAR_KEY = -1;
const CUSTOM_KEY = -2;

const DURATIONS: Duration[] = [
  { hours: 24 },
  { days: 7 },
  { days: 14 },
  { days: 30 },
  { days: 90 },
];

const customLabelMessage = defineMessage({
  id: "component.quickFilters.nextActivity.custom",
  defaultMessage: "Custom",
  description: "Custom option in dropdown in Next Activity Quick filter",
});

interface Props {
  fieldName?: string;
  filter: FilterModel;
  onSetCustomFilters: (customFilters: Partial<FilterModel>) => void;
  onUpdateFilterModel: (filterModel: Partial<FilterModel>) => void;
}

const NextActivityFilter: React.FC<Props> = ({
  fieldName = "upcomingActivity.startAt",
  filter,
  onSetCustomFilters,
  onUpdateFilterModel,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const [duration, setDuration] = useState<Duration | undefined>(undefined);

  const handleMenuClick = useCallback(
    ({ key }) => {
      const value = parseInt(key, 10);
      if (value === CUSTOM_KEY) {
        onSetCustomFilters({
          [fieldName]: { operator: FilterOperator.INTERVAL_WITHIN_NEXT, value: undefined },
        });
        analytics.clicked(["Next Activity", "Custom"]);
        return;
      }
      onUpdateFilterModel({
        [fieldName]:
          value === CLEAR_KEY
            ? undefined
            : // change this filter when we have relative dates filtering
              {
                operator: FilterOperator.INTERVAL_WITHIN_NEXT,
                value: getRangeByDuration(DURATIONS[value]),
              },
      });
      analytics.clicked(["Next Activity", JSON.stringify(DURATIONS[value])]);
    },
    [analytics, fieldName, onSetCustomFilters, onUpdateFilterModel]
  );

  useEffect(() => {
    if (
      filter &&
      fieldName in filter &&
      isSimpleCondition(filter[fieldName]) &&
      filter[fieldName].operator === FilterOperator.INTERVAL_WITHIN_NEXT
    ) {
      const filterCondition = filter[fieldName] as SimpleCondition;
      // this comparison doesn't work well when this is a saved filter, so we'll
      // have to use relative dates filtering in a future
      const foundOption = DURATIONS.find((option) => {
        const filterValue = filterCondition.value;
        const optionValue = getRangeByDuration(option);
        return optionValue.unit === filterValue.unit && optionValue.to === filterValue.to;
      });
      setDuration(foundOption);
    } else {
      setDuration(undefined);
    }
  }, [fieldName, filter]);

  return (
    <Dropdown
      overlay={
        <Menu onClick={handleMenuClick}>
          {!!duration && (
            <Menu.Item key={CLEAR_KEY}>
              <b>
                {intl.formatMessage({
                  id: "component.quickFilters.nextActivity.clear",
                  defaultMessage: "Clear filter",
                  description: "Clear option in dropdown in Next Activity Quick filter",
                })}
              </b>
            </Menu.Item>
          )}
          {DURATIONS.map((option, index) => (
            <Menu.Item key={index}>
              {intl.formatMessage(
                {
                  id: "component.quickFilters.nextActivity.option",
                  defaultMessage: "in {duration}",
                  description: "Option label in dropdown in Next Activity Quick filter",
                },
                { duration: formatDuration(option) }
              )}
            </Menu.Item>
          ))}
          <Menu.Item key={CUSTOM_KEY}>{intl.formatMessage(customLabelMessage)}</Menu.Item>
        </Menu>
      }
      trigger={["click"]}
    >
      <RoundButton active={duration !== undefined} hasMenu>
        {intl.formatMessage(
          {
            id: "component.quickFilters.nextActivity.title",
            defaultMessage: "Next activity{isSet, select, true {: in next {duration}} other {}}",
            description: "Next Activity quick filter button's title",
          },
          {
            duration: duration !== undefined ? formatDuration(duration) : "",
            isSet: duration !== undefined,
          }
        )}
      </RoundButton>
    </Dropdown>
  );
};

export default NextActivityFilter;
